import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Paper,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowUpRight,
  IconChartBar,
  IconChecklist,
  IconClockHour4,
  IconFileInvoice,
  IconPackage,
  IconReceipt2,
  IconRefresh,
  IconRotate,
  IconShoppingCart,
  IconTrendingDown,
  IconTrendingUp,
    IconTruckDelivery, IconArrowLeft
} from '@tabler/icons-react';

const monthlySpend = [
  { month: 'Oca', amount: 182000 },
  { month: 'Şub', amount: 246000 },
  { month: 'Mar', amount: 319000 },
  { month: 'Nis', amount: 287000 },
  { month: 'May', amount: 364000 },
  { month: 'Haz', amount: 402000 },
];

const departmentSpend = [
  { department: 'Bilgi İşlem', amount: 482000, share: 34 },
  { department: 'Şantiye Operasyon', amount: 391000, share: 28 },
  { department: 'İdari İşler', amount: 244000, share: 17 },
  { department: 'Muhasebe', amount: 166000, share: 12 },
  { department: 'İnsan Kaynakları', amount: 121000, share: 9 },
];

const supplierPerformance = [
  {
    vendor: 'OfisDonanım Ltd.',
    spend: 428000,
    avgLeadTime: 4,
    returnRate: 2.1,
    onTimeRate: 96,
    winRate: 41,
  },
  {
    vendor: 'Anadolu Endüstri',
    spend: 352000,
    avgLeadTime: 6,
    returnRate: 4.8,
    onTimeRate: 89,
    winRate: 28,
  },
  {
    vendor: 'Tekno Tedarik A.Ş.',
    spend: 301000,
    avgLeadTime: 5,
    returnRate: 1.9,
    onTimeRate: 93,
    winRate: 22,
  },
  {
    vendor: 'Kurumsal Market',
    spend: 188000,
    avgLeadTime: 3,
    returnRate: 3.7,
    onTimeRate: 91,
    winRate: 17,
  },
];

const topItems = [
  { item: 'Dell Latitude 15 Laptop', qty: 18, spend: 756000, category: 'IT Ekipman' },
  { item: 'A4 Fotokopi Kağıdı', qty: 420, spend: 184800, category: 'Ofis Sarf' },
  { item: 'Reflektif Yelek', qty: 210, spend: 126000, category: 'İSG' },
  { item: 'Koruyucu Baret', qty: 175, spend: 105000, category: 'İSG' },
  { item: 'Laptop Çantası', qty: 36, spend: 63000, category: 'IT Ekipman' },
];

const processStats = {
  totalRequests: 186,
  approvedRequests: 154,
  rejectedRequests: 18,
  pendingRequests: 14,
  totalQuotes: 412,
  selectedQuotes: 148,
  goodsReceipts: 132,
  returns: 11,
  invoices: 127,
  matchedInvoices: 119,
};

const budgetSummary = [
  { department: 'Bilgi İşlem', budget: 600000, actual: 482000 },
  { department: 'Şantiye Operasyon', budget: 450000, actual: 391000 },
  { department: 'İdari İşler', budget: 260000, actual: 244000 },
  { department: 'Muhasebe', budget: 180000, actual: 166000 },
];

const currency = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  maximumFractionDigits: 0,
});

function KpiCard({ title, value, subtitle, color, icon: Icon, trend, trendType = 'up' }) {
  return (
    <Card withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start" mb="sm">
        <Stack gap={4}>
          <Text size="sm" c="dimmed" fw={500}>
            {title}
          </Text>
          <Text fw={800} size="1.7rem" lh={1.1}>
            {value}
          </Text>
          <Text size="xs" c="dimmed">
            {subtitle}
          </Text>
        </Stack>

        <ThemeIcon color={color} variant="light" size={48} radius="md">
          <Icon size={22} />
        </ThemeIcon>
      </Group>

      {trend ? (
        <Group gap={6}>
          {trendType === 'up' ? (
            <IconTrendingUp size={16} color="var(--mantine-color-teal-6)" />
          ) : (
            <IconTrendingDown size={16} color="var(--mantine-color-red-6)" />
          )}
          <Text size="xs" c={trendType === 'up' ? 'teal' : 'red'} fw={600}>
            {trend}
          </Text>
        </Group>
      ) : null}
    </Card>
  );
}

function SectionCard({ title, description, children, action }) {
  return (
    <Paper withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start" mb="md">
        <div>
          <Title order={4}>{title}</Title>
          {description ? (
            <Text size="sm" c="dimmed" mt={4}>
              {description}
            </Text>
          ) : null}
        </div>
        {action}
      </Group>
      {children}
    </Paper>
  );
}

function MiniBarChart({ data }) {
  const max = Math.max(...data.map((d) => d.amount), 1);
  return (
    <Group align="flex-end" gap="xs" wrap="nowrap" style={{ height: 220 }}>
      {data.map((item) => {
        const height = Math.max((item.amount / max) * 180, 18);
        return (
          <Stack key={item.month} gap={6} align="center" style={{ flex: 1, justifyContent: 'flex-end' }}>
            <Text size="xs" fw={700} c="dimmed">
              {Math.round(item.amount / 1000)}K
            </Text>
            <div
              style={{
                width: '100%',
                height,
                minWidth: 24,
                borderRadius: 10,
                background: 'linear-gradient(180deg, var(--mantine-color-blue-4), var(--mantine-color-cyan-5))',
              }}
            />
            <Text size="xs" c="dimmed">
              {item.month}
            </Text>
          </Stack>
        );
      })}
    </Group>
  );
}

export default function ProcurementAnalyticsDashboardPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('LAST_6_MONTHS');
  const [departmentFilter, setDepartmentFilter] = useState('ALL');

  const totals = useMemo(() => {
    const totalSpend = monthlySpend.reduce((acc, item) => acc + item.amount, 0);
    const avgLead = Math.round(
      supplierPerformance.reduce((acc, item) => acc + item.avgLeadTime, 0) / supplierPerformance.length
    );
    const avgReturnRate = (
      supplierPerformance.reduce((acc, item) => acc + item.returnRate, 0) / supplierPerformance.length
    ).toFixed(1);
    const invoiceMatchRate = Math.round((processStats.matchedInvoices / processStats.invoices) * 100);

    return {
      totalSpend,
      avgLead,
      avgReturnRate,
      invoiceMatchRate,
    };
  }, []);

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Paper
          withBorder
          radius="xl"
          p="xl"
          shadow="sm"
          style={{
            background: 'linear-gradient(135deg, rgba(245,247,250,1) 0%, rgba(255,255,255,1) 100%)',
          }}
        >
          <Group justify="space-between" align="flex-start" gap="xl">
            <div>
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate("/purchase-dashboard")}
              >
                Geri
              </Button>
              <Badge variant="light" color="indigo" leftSection={<IconChartBar size={14} />}>
                Satın Alma Analitik Dashboardu
              </Badge>
              <Title order={1} mt="md">
                Analitik Dashboard
              </Title>
              <Text size="sm" c="dimmed" mt="sm" maw={900}>
                Talep, teklif, mal kabul, iade ve fatura süreçlerinden türetilen yönetimsel içgörüler.
                Bu ekran satın alma performansını, tedarikçi kalitesini, harcama dağılımını ve süreç sağlığını tek noktadan izlemek için tasarlandı.
              </Text>
            </div>

            <Group>
              <Select
                w={170}
                value={period}
                onChange={(value) => setPeriod(value || 'LAST_6_MONTHS')}
                data={[
                  { value: 'THIS_MONTH', label: 'Bu Ay' },
                  { value: 'LAST_3_MONTHS', label: 'Son 3 Ay' },
                  { value: 'LAST_6_MONTHS', label: 'Son 6 Ay' },
                  { value: 'THIS_YEAR', label: 'Bu Yıl' },
                ]}
              />
              <Select
                w={180}
                value={departmentFilter}
                onChange={(value) => setDepartmentFilter(value || 'ALL')}
                data={[
                  { value: 'ALL', label: 'Tüm Departmanlar' },
                  { value: 'IT', label: 'Bilgi İşlem' },
                  { value: 'OPS', label: 'Şantiye Operasyon' },
                  { value: 'ADMIN', label: 'İdari İşler' },
                ]}
              />
              <Button variant="default" leftSection={<IconRefresh size={16} />}>
                Yenile
              </Button>
            </Group>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, xl: 4 }} spacing="md" mt="xl">
            <KpiCard
              title="Toplam Satın Alma Hacmi"
              value={currency.format(totals.totalSpend)}
              subtitle="Seçili dönem toplamı"
              color="blue"
              icon={IconShoppingCart}
              trend="%14 artış"
            />
            <KpiCard
              title="Ortalama Termin"
              value={`${totals.avgLead} gün`}
              subtitle="Tedarikçi ortalaması"
              color="orange"
              icon={IconTruckDelivery}
              trend="1 gün iyileşme"
            />
            <KpiCard
              title="İade Oranı"
              value={`${totals.avgReturnRate}%`}
              subtitle="Kalite / teslimat etkisi"
              color="red"
              icon={IconRotate}
              trend="%0.6 düşüş"
              trendType="down"
            />
            <KpiCard
              title="Fatura Eşleşme Oranı"
              value={`${totals.invoiceMatchRate}%`}
              subtitle="3-way matching başarısı"
              color="teal"
              icon={IconFileInvoice}
              trend="%5 artış"
            />
          </SimpleGrid>
        </Paper>

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, xl: 8 }}>
            <Stack gap="lg">
              <SectionCard
                title="Aylık Satın Alma Hacmi"
                description="Seçili dönemdeki toplam satın alma tutarının aylık kırılımı."
                action={<Badge variant="light">6 Aylık Görünüm</Badge>}
              >
                <MiniBarChart data={monthlySpend} />
              </SectionCard>

              <SectionCard
                title="Süreç Sağlığı"
                description="Talep → teklif → mal kabul → fatura zincirindeki operasyonel durum göstergeleri."
              >
                <SimpleGrid cols={{ base: 1, md: 2, xl: 4 }} spacing="md">
                  <Paper withBorder radius="md" p="md">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Toplam Talep</Text>
                      <ThemeIcon color="blue" variant="light"><IconChecklist size={16} /></ThemeIcon>
                    </Group>
                    <Text fw={800} size="xl" mt="xs">{processStats.totalRequests}</Text>
                    <Text size="xs" c="dimmed" mt={4}>{processStats.pendingRequests} bekleyen</Text>
                  </Paper>

                  <Paper withBorder radius="md" p="md">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Seçilen Teklif</Text>
                      <ThemeIcon color="teal" variant="light"><IconReceipt2 size={16} /></ThemeIcon>
                    </Group>
                    <Text fw={800} size="xl" mt="xs">{processStats.selectedQuotes}</Text>
                    <Text size="xs" c="dimmed" mt={4}>{processStats.totalQuotes} tekliften</Text>
                  </Paper>

                  <Paper withBorder radius="md" p="md">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Mal Kabul</Text>
                      <ThemeIcon color="cyan" variant="light"><IconPackage size={16} /></ThemeIcon>
                    </Group>
                    <Text fw={800} size="xl" mt="xs">{processStats.goodsReceipts}</Text>
                    <Text size="xs" c="dimmed" mt={4}>{processStats.returns} iade kaydı</Text>
                  </Paper>

                  <Paper withBorder radius="md" p="md">
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Fatura</Text>
                      <ThemeIcon color="grape" variant="light"><IconFileInvoice size={16} /></ThemeIcon>
                    </Group>
                    <Text fw={800} size="xl" mt="xs">{processStats.invoices}</Text>
                    <Text size="xs" c="dimmed" mt={4}>{processStats.matchedInvoices} eşleşen</Text>
                  </Paper>
                </SimpleGrid>
              </SectionCard>

              <SectionCard
                title="Departman Bazlı Harcama"
                description="Bütçe yönetimi açısından en fazla satın alma hacmine sahip bölümler."
              >
                <Stack gap="md">
                  {departmentSpend.map((item) => (
                    <Paper key={item.department} withBorder radius="md" p="md">
                      <Group justify="space-between" mb="xs">
                        <div>
                          <Text fw={700}>{item.department}</Text>
                          <Text size="sm" c="dimmed">Toplam harcama payı %{item.share}</Text>
                        </div>
                        <Text fw={800}>{currency.format(item.amount)}</Text>
                      </Group>
                      <Progress value={item.share} radius="xl" size="lg" />
                    </Paper>
                  ))}
                </Stack>
              </SectionCard>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, xl: 4 }}>
            <Stack gap="lg">
              <SectionCard
                title="Tedarikçi Performansı"
                description="Termin, iade oranı ve teklif kazanma başarısı bazında tedarikçi görünümü."
              >
                <Stack gap="sm">
                  {supplierPerformance.map((supplier) => (
                    <Paper key={supplier.vendor} withBorder radius="md" p="md">
                      <Group justify="space-between" align="flex-start" mb="sm">
                        <div>
                          <Text fw={700}>{supplier.vendor}</Text>
                          <Text size="xs" c="dimmed" mt={4}>{currency.format(supplier.spend)} hacim</Text>
                        </div>
                        <Badge variant="light" color="blue">Kazanma %{supplier.winRate}</Badge>
                      </Group>

                      <Stack gap={8}>
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">Termin</Text>
                          <Text size="sm" fw={600}>{supplier.avgLeadTime} gün</Text>
                        </Group>
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">Zamanında Teslim</Text>
                          <Text size="sm" fw={600}>%{supplier.onTimeRate}</Text>
                        </Group>
                        <Progress value={supplier.onTimeRate} size="sm" radius="xl" color="teal" />
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">İade Oranı</Text>
                          <Text size="sm" fw={600}>%{supplier.returnRate}</Text>
                        </Group>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </SectionCard>

              <SectionCard
                title="Bütçe Kullanımı"
                description="Departman bütçelerine göre gerçekleşen harcama görünümü."
              >
                <Stack gap="sm">
                  {budgetSummary.map((item) => {
                    const usage = Math.round((item.actual / item.budget) * 100);
                    return (
                      <Paper key={item.department} withBorder radius="md" p="md">
                        <Group justify="space-between" mb="xs">
                          <Text fw={700}>{item.department}</Text>
                          <Badge variant="light" color={usage > 90 ? 'red' : usage > 75 ? 'orange' : 'teal'}>
                            %{usage}
                          </Badge>
                        </Group>
                        <Progress value={usage} radius="xl" size="lg" color={usage > 90 ? 'red' : usage > 75 ? 'orange' : 'teal'} />
                        <Group justify="space-between" mt="xs">
                          <Text size="xs" c="dimmed">Bütçe: {currency.format(item.budget)}</Text>
                          <Text size="xs" c="dimmed">Gerçekleşen: {currency.format(item.actual)}</Text>
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>
              </SectionCard>

              <SectionCard
                title="Alarm ve Uyarılar"
                description="Operasyonel risk üreten anlık göstergeler."
              >
                <Stack gap="sm">
                  <Paper withBorder radius="md" p="md" bg="red.0">
                    <Group wrap="nowrap" align="flex-start">
                      <ThemeIcon color="red" variant="light" mt={2}><IconAlertTriangle size={16} /></ThemeIcon>
                      <div>
                        <Text fw={700} size="sm">İade oranı yükselen tedarikçi</Text>
                        <Text size="xs" c="dimmed" mt={4}>Anadolu Endüstri için son 30 günde iade oranı %4.8 seviyesinde.</Text>
                      </div>
                    </Group>
                  </Paper>
                  <Paper withBorder radius="md" p="md" bg="orange.0">
                    <Group wrap="nowrap" align="flex-start">
                      <ThemeIcon color="orange" variant="light" mt={2}><IconClockHour4 size={16} /></ThemeIcon>
                      <div>
                        <Text fw={700} size="sm">Bekleyen onay yoğunluğu</Text>
                        <Text size="xs" c="dimmed" mt={4}>14 talep halen onay bekliyor, ortalama bekleme süresi 1.8 gün.</Text>
                      </div>
                    </Group>
                  </Paper>
                  <Paper withBorder radius="md" p="md" bg="blue.0">
                    <Group wrap="nowrap" align="flex-start">
                      <ThemeIcon color="blue" variant="light" mt={2}><IconArrowUpRight size={16} /></ThemeIcon>
                      <div>
                        <Text fw={700} size="sm">Harcamada artış</Text>
                        <Text size="xs" c="dimmed" mt={4}>Bilgi İşlem departmanı harcaması son dönemde %12 arttı.</Text>
                      </div>
                    </Group>
                  </Paper>
                </Stack>
              </SectionCard>
            </Stack>
          </Grid.Col>
        </Grid>

        <SectionCard
          title="En Çok Satın Alınan Kalemler"
          description="Satın alma hacmi ve ürün yoğunluğu en yüksek olan kalemler."
        >
          <Table highlightOnHover striped verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Kalem</Table.Th>
                <Table.Th>Kategori</Table.Th>
                <Table.Th>Miktar</Table.Th>
                <Table.Th>Toplam Harcama</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {topItems.map((item) => (
                <Table.Tr key={item.item}>
                  <Table.Td>
                    <Text fw={700}>{item.item}</Text>
                  </Table.Td>
                  <Table.Td>{item.category}</Table.Td>
                  <Table.Td>{item.qty}</Table.Td>
                  <Table.Td>
                    <Text fw={700}>{currency.format(item.spend)}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </SectionCard>

        <Paper withBorder p="md" radius="lg" shadow="sm" style={{ borderStyle: 'dashed' }}>
          <Text size="sm" c="dimmed">
            Bu dashboard, satın alma modülünün yönetim katmanıdır. Bir sonraki adımda bu ekranı gerçek API’lerle besleyip grafik bileşenleri,
            filtreli drill-down görünümleri ve tarih kırılımlı detay raporlarla güçlendirebiliriz.
          </Text>
        </Paper>
      </Stack>
    </Container>
  );
}
